"use client";
import Circlesbg from "@/app/[locale]/components/bgs/Circlesbg";
import MainSectionMask from "@/app/[locale]/components/bgs/MainSectionMask";
import { CousrsDetailsType } from "@/app/types";
import React, { useState } from "react";
import PaymentCard from "./PaymentCard";
import * as Yup from "yup";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";

import { useLocale, useTranslations } from "next-intl";
import MainInformation from "./MainInformation";
import { FormikProvider, useFormik } from "formik";
import TextInput from "@/app/[locale]/components/inputs/TextInput";
import PhoneNumberInput from "@/app/[locale]/components/inputs/PhoneNumberInput";
import SelectAccordion from "./SelectAccordion";
import PromoCode from "./PromoCode";
import {
  canceledPaypalPayment,
  capturePaypalPayment,
  classEnrollSignUp,
  createPaypalPayment,
  createStripePayment,
  formatDateToMonthDay,
} from "@/app/utils/payment";
import { useRouter, useSearchParams } from "next/navigation";
import Loader from "@/app/[locale]/components/Feedbacks/Loader";
import { toast } from "react-toastify";
import { PAYPAL_CLIENT_ID } from "@/app/env";
import { isValidPhoneNumber } from "react-phone-number-input";
function EnrollPage({ course }: { course: CousrsDetailsType }) {
  const searchParams = useSearchParams();
  const searchSolt = searchParams.get("solt") || "";
  const initialSolt = course.dateSlots.find(
    (solt) => `${solt.id}` == searchSolt
  );

  const initialValues = {
    studentName: "",
    parentName: "",
    parentEmail: "",
    parentJob: "",
    parentPhone: "",
    classType: initialSolt?.type || "Group",
    time: searchSolt || "",
  };
  const t = useTranslations("contactUs.form");
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [paymentIntentId, setPaymentIntentId] = useState("");
  const [promoCode, setPromoCode] = useState<string | null>(null);
  const [newPrice, setNewPrice] = useState<number>();
  const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;

  const local = useLocale();
  const validationSchema = Yup.object().shape({
    studentName: Yup.string()
      .required(t("required"))
      .min(2, t("invalid"))
      .max(30, t("invalid")),
    parentName: Yup.string()
      .required(t("required"))
      .min(2, t("invalid"))
      .max(30, t("invalid")),
    parentEmail: Yup.string()
      .required(t("required"))
      .email(t("invalid"))
      .trim()
      .matches(emailRegex, t("invalid"))
      .min(2, t("invalid"))
      .max(40, t("invalid")),

    parentJob: Yup.string()
      .required(t("required"))
      .min(2, t("invalid"))
      .max(30, t("invalid")),
    parentPhone: Yup.string()
      .required(t("required"))

      .test("valid-phone-number", t("invalid"), (value) =>
        value ? isValidPhoneNumber(value) : true
      ),
    classType: Yup.string().required(t("required")),
    time: Yup.string().required(t("required")),
  });

  const formik = useFormik({
    initialValues,
    validateOnChange: true,
    validationSchema,
    onSubmit: (values) => {},
  });
  const slot = course.dateSlots.find(
    (item) => `${item.id}` == formik.values.time
  );

  const orginalCost =
    formik.values.classType == "Private"
      ? parseFloat(course.privateMonthlyPrice)
      : parseFloat(course.groupMonthlyPrice);
  const price = newPrice ? newPrice : orginalCost;

  const courseTypesOptions = [
    {
      key: `${t("shared")} | ${t("up")} |${course.groupMonthlyPrice}€/${t(
        "month"
      )}`,
      value: "Group",
    },
    {
      key: `${t("private")} | ${t("only")} | ${course.privateMonthlyPrice}€/${t(
        "month"
      )}`,
      value: "Private",
    },
  ];
  const courseTimes = course.dateSlots
    .filter((item) => item.type == formik.values.classType)
    .map((date) => ({
      key: `${date.day}`,
      value: `${date.id}`,
    }));

  const handleClassEnrollSignUp = async () => {
    const isValid = await validationSchema.isValid(formik.values);
    formik.submitForm();
    if (isValid) {
      try {
        const values = formik.values;
        const slot = course.dateSlots.find(
          (item) => `${item.id}` == values.time
        );
        const userData = await classEnrollSignUp({
          classType: values.classType,
          parentEmail: values.parentEmail,
          parentJob: values.parentJob,
          parentName: values.parentName,
          parentPhone: values.parentPhone,
          studentName: values.studentName,
          time: slot?.fromDate ?? "",
        });
        setToken(userData.data.accessToken);
        if (!userData.data.accessToken) {
          toast.error(t("error"), {
            position: "bottom-center",
          });
          setLoading(false);
        }
        return userData.data.accessToken;
      } catch {
        setLoading(false);
        toast.error(t("error"), {
          position: "bottom-center",
        });
      }
    }
  };

  const createOrder = async (values: any) => {
    const slot = course.dateSlots.find((item) => `${item.id}` == values.time);
    if (token) {
      return createPaypalPayment(
        {
          currency: "eur",
          lang: local,
          ...(promoCode ? { promotionCode: promoCode } : {}),
          items: [
            {
              dateSlotId: slot?.id ?? 1,
              id: slot?.courseId ?? 1,
              price: price,
              courseType: `${CLASS_TYPES_VALUES[formik.values.classType]}`,
            },
          ],
        },
        token
      )
        .then((data) => {
          if (data.data.orderId) return data.data.orderId;
          toast.error(t("error"), {
            position: "bottom-center",
          });
        })
        .catch(() => {
          toast.error(t("error"), {
            position: "bottom-center",
          });
        });
    }
  };
  const router = useRouter();

  const paypalOnApprove = async (orderID: string) => {
    return await capturePaypalPayment(orderID, token).then((res) => {
      router.push(`/courses/${course.id}/enroll/completion?payment_way=paypal`);
    });
  };
  const paypalOnCanceled = async (orderID: string) => {
    return await canceledPaypalPayment(orderID, token).then((res) => {
      toast.error(t("canceled"), {
        position: "bottom-center",
      });
    });
  };
  const handleChosePaypal = async ({
    onSuccess,
  }: {
    onSuccess: () => void;
  }) => {
    setLoading(true);
    const token = await handleClassEnrollSignUp();
    setLoading(false);

    if (token) {
      onSuccess();
    }
  };
  const handleChosePayWithCard = async ({
    onSuccess,
  }: {
    onSuccess: () => void;
  }) => {
    setLoading(true);
    const _token = await handleClassEnrollSignUp();
    if (_token) {
      var body = {
        currency: "eur",
        lang: local,
        ...(promoCode ? { promotionCode: promoCode } : {}),

        items: [
          {
            courseType: `${CLASS_TYPES_VALUES[formik.values.classType]}`,

            dateSlotId: slot?.id ?? 1,
            id: slot?.courseId ?? 1,
            price: price,
          },
        ],
      };

      const data = await createStripePayment(body, _token);
      setClientSecret(data.data.clientSecret);
      setPaymentIntentId(data.data.paymentIntentId);
      if (data.data.clientSecret) {
        onSuccess();
      } else {
        toast.error(t("error"), {
          position: "bottom-center",
        });
      }
    }
    setLoading(false);
  };
  const CLASS_TYPES_VALUES: { [key: string]: number } = {
    Private: 2,
    Group: 1,
  };

  return (
    <PayPalScriptProvider
      options={{ clientId: PAYPAL_CLIENT_ID, currency: "EUR" }}
    >
      {loading && <Loader />}
      <div className="bg-[#FAFAFA]">
        {" "}
        <Circlesbg>
          <MainSectionMask color={course?.color} />
          <div className="flex lg:flex-row flex-col pb-5  lg:gap-[7.4rem]  container lg:!pe-[2rem] pt-[107px] lg:pt-[6.25]">
            <div>
              <MainInformation course={course} />
              <div className="lg:pt-[55.5px] pt-[67px]">
                <div className="flex flex-col gap-7 lg:w-[44rem] ">
                  <form>
                    <FormikProvider value={formik}>
                      <TextInput
                        type="text"
                        label={t("studentName")}
                        color={course.color}
                        placeholder={t("studentName")}
                        name="studentName"
                        labelClassName="lg:!w-[20rem]"
                      />
                      <TextInput
                        type="text"
                        label={t("parentName")}
                        color={course.color}
                        placeholder={t("parentName")}
                        name="parentName"
                        labelClassName="lg:!w-[20rem]"
                      />
                      <TextInput
                        type="text"
                        name="parentJob"
                        color={course.color}
                        label={t("parentJob")}
                        placeholder={t("parentJob")}
                        labelClassName="lg:!w-[20rem]"
                      />
                      <TextInput
                        name="parentEmail"
                        type="email"
                        label={t("parentEmail")}
                        color={course.color}
                        placeholder={t("parentEmail")}
                        labelClassName="lg:!w-[20rem]"
                      />
                      <PhoneNumberInput
                        labelClassName="lg:!w-[20rem]"
                        color={course.color}
                        onChange={(value) =>
                          formik.setFieldValue("parentPhone", value)
                        }
                        name="parentPhone"
                        label={t("parentPhone")}
                      />{" "}
                      <SelectAccordion
                        labelClassName="lg:!w-[20rem]"
                        value={formik.values.classType}
                        color={course.color}
                        onChange={(v) => {
                          formik.setFieldValue("classType", v);
                          formik.setFieldValue("time", "");
                        }}
                        name="classType"
                        placeholder={t("classType")}
                        label={t("classType")}
                        options={courseTypesOptions}
                      />
                      <SelectAccordion
                        labelClassName="lg:!w-[20rem]"
                        color={course.color}
                        value={formik.values.time}
                        onChange={(v) => formik.setFieldValue("time", v)}
                        name="time"
                        placeholder={t("chooseTime")}
                        label={t("chooseTime")}
                        options={courseTimes}
                      />
                      <PromoCode
                        color={course.color}
                        courseType={CLASS_TYPES_VALUES[formik.values.classType]}
                        onSuccess={(price, code) => {
                          setNewPrice(price);
                          setPromoCode(code);
                        }}
                      />
                    </FormikProvider>
                  </form>
                </div>
              </div>
            </div>

            <PaymentCard
              clientSecret={clientSecret}
              orginalCost={price !== orginalCost ?orginalCost:undefined}
              price={price}
              forceReRender={[
                formik.values.classType,
                formik.values.time,
                formik.isValid,

                price,
                token,
              ]}
              values={formik.values}
              promoCode={promoCode}
              handleChosePayWithCard={handleChosePayWithCard}
              handleChosePaypal={handleChosePaypal}
              disabled={!token}
              paypalOnApprove={paypalOnApprove}
              paypalOncCanceled={paypalOnCanceled}
              createOrder={() => createOrder(formik.values)}
            />
          </div>
        </Circlesbg>
      </div>
    </PayPalScriptProvider>
  );
}

export default EnrollPage;
